
import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';
import { SA_USER_PROFILE, SA_USER_SIGNOUT, LOGIN_ENDPOINT } from '../../shared/constants/apiEndpoints';
import { useEncryptService } from '../../shared/hooks';
import axios from 'axios';
import { get } from 'lodash';

interface User {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  last_login: string;
  profile_image_url: string;
  is_real_user: boolean;
  organization_id?: string;
}

interface AuthContextType {
  user: User | null;
  role?: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, newsession: any) => Promise<any>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const { getAuthHeader, doEncryptLogin } = useEncryptService();

  const isAuthenticated = !!user;

  // Check auth on app load
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");

      if (!token) {
        setUser(null);
        // setRole("");
        setIsLoading(false);
        return;
      }

      // Set token in axios
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Fetch user profile
      const response = await axiosInstance.get(SA_USER_PROFILE);
      setUser(response.data.data);
      setIsLoading(false);
      console.log("=== USER PROFILE API RESPONSE ===", response);

      // const userData = response.data.data;
      // setUser(userData);
      // setRole(userData?.role || "");
    } catch (error: any) {
      console.error("Auth check failed:", error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log("Authentication failed - clearing auth");
        clearAuth();
      } else {
        // fallback to stored user if available
        try {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
             const userData = JSON.parse(storedUserData);
            setUser(userData);
            console.log('Using stored user data:', userData);
          } else if (!user) {
            // If no stored data and no current user, create minimal user object
            setUser({ 
            user_id: 'unknown', 
              email: 'unknown', 
              first_name: 'User', 
              last_name: '', 
              full_name: 'User',
              role: 'user',
              last_login: '',
              profile_image_url: '',
              is_real_user: false
            });
          }
        } catch (parseError) {
           console.error('Error parsing stored user data:', parseError);
          // Keep existing user state or set minimal user if none exists
          if (!user) {
            setUser({ 
              user_id: 'unknown', 
              email: 'unknown', 
              first_name: 'User', 
              last_name: '', 
              full_name: 'User',
              role: 'user',
              last_login: '',
              profile_image_url: '',
              is_real_user: false
            });
          }
          console.error("Error parsing stored user data:", parseError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, newsession: any): Promise<any> => {
    try {
      const { doEncryptLogin, getAuthHeader } = useEncryptService();
      setIsLoading(true);

      // Encrypt password
      const encryptedPassword = doEncryptLogin(password);

      const requestPayload = {
        username: email,
        password: encryptedPassword,
        ui_type: "super_admin",
        timezone: "UTC",
        new_session: true
      };

      if (newsession === true) {
 Object.assign(requestPayload, { new_session: true });      }

      const httpOptions = getAuthHeader();

      const response = await axiosInstance.post(LOGIN_ENDPOINT, requestPayload, httpOptions);
      console.log("=== LOGIN API RESPONSE ===", response);

      if (response.data?.status === 200) {
        if (response.data?.data?.active_session) {
          setIsLoading(false);
          return { active_session: true };
        }

        const token = response.data?.data?.access_token;
        if (token) {
          localStorage.setItem("authToken", token);
          axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          console.log("Token stored and set in headers");
        }
         const userData = response.data.data?.user || response.data.data || response.data;
        setUser(userData);
        
        // Store user data in localStorage for persistence
        localStorage.setItem('userData', JSON.stringify(userData));

        // 🚨 Immediately fetch user profile after login
        await checkAuth();

        return {};
      } else {
        console.error("Login failed:", response.data);
        // Return the error message from the API response
        return { 
          success: false, 
          message: response.data?.message || "Login failed. Please try again." 
        };
      }
    } catch (error: any) {
      console.error("Login failed:", error);
      // Return the error message from the API response
      const errorMessage = error.response?.data?.message || error.message || "Login failed. Please try again.";
      return { 
        success: false, 
        message: errorMessage 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("authToken");
      console.log("Logout - Using Bearer token:", token ? `Bearer ${token}` : "No token");
      const httpOptions = getAuthHeader();
      await axiosInstance.get(`${SA_USER_SIGNOUT}?token=${token}`, httpOptions);
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      clearAuth();
    }
  };

  const clearAuth = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    delete axiosInstance.defaults.headers.common["Authorization"];
    setUser(null);
    setRole("");
  };

  const value: AuthContextType = {
    user,
    role,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
    clearAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
